import React, { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import '../css.css';

// Bar Graph 컴포넌트
const BarGraph = ({ value, minValue, maxValue, label }) => {
    const isDarkMode = document.body.classList.contains('dark-mode');
    
    // 값이 0이거나 null이면 '상점 없음' 표시
    if (value === null || value === undefined || value === 0 || value === "-") {
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '10px',
                fontSize: '11px',
                color: isDarkMode ? '#999' : '#666',
                fontStyle: 'italic'
            }}>
                상점 없음
            </div>
        );
    }

    // 범위가 없으면 표시하지 않음
    if (maxValue === minValue) {
        return (
            <div style={{ textAlign: 'center', padding: '10px' }}>
                {value}
            </div>
        );
    }

    // 최소값을 1/3 지점으로, 최대값을 100%로 설정
    // 100을 기준값으로 하여 표시선 위치 계산
    const baseValue = 100;
    const basePercentage = 40; // 기준값(100)이 40% 지점

    // 값이 100이면 정확히 40%, 100보다 작으면 40% 지점 아래로, 크면 위로
    let percentage;
    const minPercentage = 5; // 최소 5%는 보장
    
    if (value === baseValue) {
        // 값이 정확히 100이면 40% 지점에 고정
        percentage = basePercentage;
    } else if (value < baseValue) {
        // 100 미만인 경우: 최소값~100 사이를 5%~40% 구간에 매핑
        const baseRange = baseValue - minValue;
        const baseRelativeValue = value - minValue;
        percentage = minPercentage + (baseRelativeValue / baseRange) * (basePercentage - minPercentage);
    } else {
        // 100 초과인 경우: 100~최대값 사이를 40%~100% 구간에 매핑
        const upperRange = maxValue - baseValue;
        const upperRelativeValue = value - baseValue;
        percentage = basePercentage + (upperRelativeValue / upperRange) * (100 - basePercentage);
    }
    
    // 색상 계산: 녹색(최소) → 노란색(중간) → 빨간색(최대)
    const getColor = (value, min, max) => {
        const normalizedValue = (value - min) / (max - min);
        if (normalizedValue <= 0.5) {
            // 녹색에서 노란색으로 (0 ~ 0.5)
            const ratio = normalizedValue / 0.5;
            if (isDarkMode) {
                // 다크모드에서는 더 어두운 색상 사용
                const red = Math.round(180 * ratio);
                const green = 180;
                return `rgb(${red}, ${green}, 0)`;
            } else {
                const red = Math.round(255 * ratio);
                const green = 255;
                return `rgb(${red}, ${green}, 0)`;
            }
        } else {
            // 노란색에서 빨간색으로 (0.5 ~ 1)
            const ratio = (normalizedValue - 0.5) / 0.5;
            if (isDarkMode) {
                // 다크모드에서는 더 어두운 색상 사용
                const red = 180;
                const green = Math.round(180 * (1 - ratio));
                return `rgb(${red}, ${green}, 0)`;
            } else {
                const red = 255;
                const green = Math.round(255 * (1 - ratio));
                return `rgb(${red}, ${green}, 0)`;
            }
        }
    };

    const barColor = getColor(value, minValue, maxValue);
    
    return (
        <div style={{ position: 'relative', width: '100%', minWidth: '100px' }}>
            {/* 배경 바 */}
            <div style={{
                width: '100%',
                height: '24px',
                backgroundColor: isDarkMode ? '#3a3a3a' : '#f0f0f0',
                border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                borderRadius: '4px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {/* 기준값(100) 표시선 (1/3 지점) */}
                <div style={{
                    position: 'absolute',
                    left: `${basePercentage}%`,
                    top: '0',
                    width: '1px',
                    height: '100%',
                    backgroundColor: isDarkMode ? '#999' : '#666',
                    zIndex: 1
                }} />
                
                {/* 실제 값 바 */}
                <div style={{
                    width: `${percentage}%`,
                    height: '100%',
                    backgroundColor: barColor,
                    transition: 'width 0.5s ease, background-color 0.5s ease',
                    minWidth: percentage > 0 ? '2px' : '0'
                }} />
            </div>
            
            {/* 값 텍스트 */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '11px',
                fontWeight: 'bold',
                color: isDarkMode ? '#f0f0f0' : '#333',
                textShadow: isDarkMode ? '1px 1px 1px rgba(0,0,0,0.8)' : '1px 1px 1px rgba(255,255,255,0.8)',
                pointerEvents: 'none',
                zIndex: 2
            }}>
                {value}
            </div>
        </div>
    );
};

const Page1 = () => {
    const [priceData, setPriceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const isDarkMode = document.body.classList.contains('dark-mode');

    const fetchPriceData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch('/api/price');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setPriceData(data);
        } catch (error) {
            console.error('Error fetching price data:', error);
            setError('가격 데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPriceData();
    }, [fetchPriceData]);

    // 각 컬럼의 최소값과 최대값 계산
    const getMinMaxValues = () => {
        if (priceData.length === 0) return {};
        
        const columns = [
            'shop_fishmarket', 'shop_tackle', 'shop_food', 'shop_hardware',
            'price_mechanism', 'time_mechanism', 'price_brake', 'time_brake'
        ];
        
        const result = {};
        columns.forEach(col => {
            const values = priceData.map(item => item[col] || 0).filter(val => val > 0);
            if (values.length > 0) {
                result[col] = {
                    min: Math.min(...values),
                    max: Math.max(...values)
                };
            } else {
                result[col] = { min: 0, max: 0 };
            }
        });
        
        return result;
    };

    const minMaxValues = getMinMaxValues();

    // 정렬 함수
    const handleSort = (key) => {
        // 맵 컬럼을 클릭하면 기본 정렬로 리셋
        if (key === 'map') {
            setSortConfig({ key: null, direction: 'asc' });
            return;
        }

        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // 정렬된 데이터
    const sortedData = React.useMemo(() => {
        if (!sortConfig.key) return priceData;

        return [...priceData].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // 문자열인 경우 (맵 이름)
            if (sortConfig.key === 'map') {
                aValue = aValue || '';
                bValue = bValue || '';
                return sortConfig.direction === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            // 숫자인 경우 (나머지 컬럼들)
            // '상점 없음' 값들(null, undefined, 0, "-")을 체크
            const aIsEmpty = aValue === null || aValue === undefined || aValue === 0 || aValue === "-";
            const bIsEmpty = bValue === null || bValue === undefined || bValue === 0 || bValue === "-";

            // 둘 다 '상점 없음'인 경우 원래 순서 유지
            if (aIsEmpty && bIsEmpty) return 0;
            
            // 하나만 '상점 없음'인 경우 아래로 보냄
            if (aIsEmpty) return 1;  // a가 '상점 없음'이면 아래로
            if (bIsEmpty) return -1; // b가 '상점 없음'이면 아래로

            // 둘 다 유효한 값인 경우 일반 정렬
            aValue = parseFloat(aValue);
            bValue = parseFloat(bValue);

            if (sortConfig.direction === 'asc') {
                return aValue - bValue;
            } else {
                return bValue - aValue;
            }
        });
    }, [priceData, sortConfig]);

    // 정렬 아이콘
    const getSortIcon = (columnKey) => {
        // 맵 컬럼은 기본 정렬 버튼
        if (columnKey === 'map') {
            return sortConfig.key === null ? '↕️' : '🔄';
        }

        if (sortConfig.key !== columnKey) {
            return '↕️';
        }
        return sortConfig.direction === 'asc' ? 
            <span style={{ color: 'green' }}>▲</span> : 
            <span style={{ color: 'red' }}>▼</span>;
    };

    if (loading) {
        return (
            <>
                <h1 className="page-title">수역별 시세</h1>
                <div className="spinner-container">
                    <LoadingSpinner
                        height={80}
                        width={80}
                        color="#4fa94d"
                        visible={true}
                    />
                </div>
                <Comments pageName="price" />
            </>
        );
    }

    if (error) {
        return (
            <>
                <h1 className="page-title">수역별 시세</h1>
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={fetchPriceData}>다시 시도</button>
                </div>
                <Comments pageName="price" />
            </>
        );
    }

    return (
        <>
            <h1 className="page-title">수역별 시세</h1>
            <div className="page-content">
                <table className="oil-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('map')} style={{ cursor: 'pointer' }}>
                                맵 {getSortIcon('map')}
                            </th>
                            <th onClick={() => handleSort('shop_fishmarket')} style={{ cursor: 'pointer' }}>
                                어시장 {getSortIcon('shop_fishmarket')}
                            </th>
                            <th onClick={() => handleSort('shop_tackle')} style={{ cursor: 'pointer' }}>
                                낚시용품점 {getSortIcon('shop_tackle')}
                            </th>
                            <th onClick={() => handleSort('shop_food')} style={{ cursor: 'pointer' }}>
                                식료품점 {getSortIcon('shop_food')}
                            </th>
                            <th onClick={() => handleSort('shop_hardware')} style={{ cursor: 'pointer' }}>
                                철물점 {getSortIcon('shop_hardware')}
                            </th>
                            <th onClick={() => handleSort('price_mechanism')} style={{ cursor: 'pointer' }}>
                                매커니즘 가격 {getSortIcon('price_mechanism')}
                            </th>
                            <th onClick={() => handleSort('time_mechanism')} style={{ cursor: 'pointer' }}>
                                매커니즘 시간 {getSortIcon('time_mechanism')}
                            </th>
                            <th onClick={() => handleSort('price_brake')} style={{ cursor: 'pointer' }}>
                                브레이크 가격 {getSortIcon('price_brake')}
                            </th>
                            <th onClick={() => handleSort('time_brake')} style={{ cursor: 'pointer' }}>
                                브레이크 시간 {getSortIcon('time_brake')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedData.map((price, index) => (
                            <tr key={price.id || index}>
                                <td style={{ 
                                    fontWeight: 'bold',
                                    color: isDarkMode ? '#e0e0e0' : 'inherit'
                                }}>{price.map}</td>
                                <td>
                                    <BarGraph 
                                        value={price.shop_fishmarket}
                                        minValue={minMaxValues.shop_fishmarket?.min || 0}
                                        maxValue={minMaxValues.shop_fishmarket?.max || 0}
                                    />
                                </td>
                                <td>
                                    <BarGraph 
                                        value={price.shop_tackle}
                                        minValue={minMaxValues.shop_tackle?.min || 0}
                                        maxValue={minMaxValues.shop_tackle?.max || 0}
                                    />
                                </td>
                                <td>
                                    <BarGraph 
                                        value={price.shop_food}
                                        minValue={minMaxValues.shop_food?.min || 0}
                                        maxValue={minMaxValues.shop_food?.max || 0}
                                    />
                                </td>
                                <td>
                                    <BarGraph 
                                        value={price.shop_hardware}
                                        minValue={minMaxValues.shop_hardware?.min || 0}
                                        maxValue={minMaxValues.shop_hardware?.max || 0}
                                    />
                                </td>
                                <td>
                                    <BarGraph 
                                        value={price.price_mechanism}
                                        minValue={minMaxValues.price_mechanism?.min || 0}
                                        maxValue={minMaxValues.price_mechanism?.max || 0}
                                    />
                                </td>
                                <td>
                                    <BarGraph 
                                        value={price.time_mechanism}
                                        minValue={minMaxValues.time_mechanism?.min || 0}
                                        maxValue={minMaxValues.time_mechanism?.max || 0}
                                    />
                                </td>
                                <td>
                                    <BarGraph 
                                        value={price.price_brake}
                                        minValue={minMaxValues.price_brake?.min || 0}
                                        maxValue={minMaxValues.price_brake?.max || 0}
                                    />
                                </td>
                                <td>
                                    <BarGraph 
                                        value={price.time_brake}
                                        minValue={minMaxValues.time_brake?.min || 0}
                                        maxValue={minMaxValues.time_brake?.max || 0}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Comments pageName="price" />
        </>
    );
};

export default Page1;